import { Tile, tileAt, type TileMap } from '../world/tilemap';

/**
 * Autotiling del terreno CraftPix "Top-Down Simple Summer".
 *
 * Ogni tile del tileset è stato classificato campionando 9 punti
 * (angoli, lati, centro) e annotando se il pixel è Sabbia o erba (Grass).
 * A runtime si calcola il pattern desiderato dai vicini della mappa e si
 * sceglie il tile più somigliante: nessuna tabella di maschere a mano.
 *
 * Ordine del pattern: NW N NE W C E SW S SE  (S = sabbia, G = erba)
 */

const GROUND_PATTERNS: Record<number, string> = {
  1: 'SSSSSSSSG',
  2: 'SSSSSSGGG',
  3: 'SSSSSSGSS',
  4: 'SSGSSGSSG',
  5: 'GGGGSGGGG',
  6: 'GSSGSSGSS',
  7: 'SSGSSSSSS',
  8: 'GGGSSSSSS',
  9: 'GSSSSSSSS',
  10: 'GGGGSSGSS',
  11: 'GSGSSSSSS',
  12: 'GGGSSGSSG',
  13: 'GSSSSSGSS',
  14: 'SSSSSSSSS',
  15: 'SSGSSSSSG',
  16: 'GSSGSSGGG',
  17: 'SSSSSSGSG',
  18: 'SSGSSGGGG',
  19: 'GGGGGSGSS',
  20: 'GGGSSSSSS',
  21: 'GGGSGGSSG',
  22: 'GSSGSSGSS',
  23: 'SSSSSSSSS',
  24: 'SSGSSGSSG',
  25: 'GSSGGSGGG',
  26: 'SSSSSSGGG',
  27: 'SSGSGGGGG',
  28: 'GGGGSSGSG',
  29: 'GGGSSSGSG',
  30: 'GGGSSGGSG',
  31: 'GSGGSSGSG',
  32: 'GSGSSSGSG',
  33: 'GSGSSGGSG',
  34: 'GSGGSSGGG',
  35: 'GSGSSSGGG',
  36: 'GSGSSGGGG',
  37: 'GGGGSGGSG',
  38: 'GSGGSGGSG',
  39: 'GSGGSGGGG',
  40: 'GGGGSSGGG',
  41: 'GGGSSSGGG',
  42: 'GGGSSGGGG',
  43: 'GGGGGGGGG',
  44: 'GGGSSSSSG',
  45: 'GGGSSSGSS',
  46: 'SSGSSSGGG',
  47: 'GSSSSSGGG',
  48: 'GGGGGSGSG',
  49: 'GGGSSSGGG',
  50: 'GGGSGGGSG',
  51: 'GSGGSGGSG',
  52: 'GGGGGGGGG',
  53: 'GSGGSGGSG',
  54: 'GSGGGSGGG',
  55: 'GGGSSSGGG',
  56: 'GSGSGGGGG',
};

export const FULL_GRASS_IDS = [43, 52];
export const FULL_SAND_IDS = [14, 23];

/** Tutti gli id del tileset, da passare al loader. */
export function allGroundIds(): number[] {
  return Object.keys(GROUND_PATTERNS).map(Number);
}

/** Terreni che il tileset rappresenta come "sabbia" (sentieri, spiagge, fondale lago). */
function isSandTerrain(tile: Tile): boolean {
  return tile === Tile.Path || tile === Tile.Sand || tile === Tile.Water;
}

// Pesi: il centro conta più dei lati, i lati più degli angoli.
const POINT_WEIGHTS = [1, 2, 1, 2, 4, 2, 1, 2, 1];

function bestMatch(desired: string, variantSeed: number): number {
  let bestScore = -1;
  const candidates: number[] = [];
  for (const [idStr, pattern] of Object.entries(GROUND_PATTERNS)) {
    let score = 0;
    for (let i = 0; i < 9; i++) {
      if (pattern[i] === desired[i]) score += POINT_WEIGHTS[i]!;
    }
    if (score > bestScore) {
      bestScore = score;
      candidates.length = 0;
      candidates.push(Number(idStr));
    } else if (score === bestScore) {
      candidates.push(Number(idStr));
    }
  }
  return candidates[variantSeed % candidates.length]!;
}

/**
 * Sceglie il tile per una cella di terreno sabbioso guardando gli 8 vicini.
 * Le celle d'erba pura usano direttamente le varianti FULL_GRASS.
 */
export function groundTileId(map: TileMap, tx: number, ty: number): number {
  const tile = tileAt(map, tx, ty);
  const seed = (tx * 31 + ty * 17) & 0x7fffffff;

  if (!isSandTerrain(tile)) {
    return FULL_GRASS_IDS[seed % FULL_GRASS_IDS.length]!;
  }

  const sand = (dx: number, dy: number): boolean => isSandTerrain(tileAt(map, tx + dx, ty + dy));
  const n = sand(0, -1);
  const e = sand(1, 0);
  const s = sand(0, 1);
  const w = sand(-1, 0);
  // un angolo è sabbia solo se entrambi i lati adiacenti e la diagonale lo sono
  const nw = n && w && sand(-1, -1);
  const ne = n && e && sand(1, -1);
  const sw = s && w && sand(-1, 1);
  const se = s && e && sand(1, 1);

  const desired =
    (nw ? 'S' : 'G') +
    (n ? 'S' : 'G') +
    (ne ? 'S' : 'G') +
    (w ? 'S' : 'G') +
    'S' +
    (e ? 'S' : 'G') +
    (sw ? 'S' : 'G') +
    (s ? 'S' : 'G') +
    (se ? 'S' : 'G');

  return bestMatch(desired, seed);
}
